import { useEffect, useRef, useState } from 'react';

// Ease-out so numbers sprint then settle — the satisfying part of a count-up.
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/**
 * Animates a number from 0 up to `target` over `duration` ms (Aurora dashboard).
 * Re-runs whenever the target changes, so values animate in once async data
 * lands. RAF-driven (no Date/Math.random), respects prefers-reduced-motion.
 */
const useCountUp = (target: number, duration = 1100): number => {
  const [value, setValue] = useState(0);
  const rafId = useRef<number>();
  const startTs = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || target === 0) {
      setValue(target);
      return undefined;
    }
    startTs.current = undefined;
    const tick = (now: number) => {
      if (startTs.current === undefined) startTs.current = now;
      const t = Math.min(1, (now - startTs.current) / duration);
      setValue(target * easeOutCubic(t));
      if (t < 1) rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
    return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
  }, [target, duration]);

  return value;
};

export default useCountUp;
